/**
 * Supply-chain coverage auto-fill — maps live `SupplyChainPlan` data into
 * the `vendor-quote-pack` Coverage Matrix requirement.
 */
import type { SupplyChainPlan } from '../../types/supplyChain';
import type { RequirementAutoFill } from './compileCoverageAutoFill';
import { bomIdsWithComparableQuotes } from '../buildQuoteComparison';
import { isDatacratePartner } from '../supplierPartnership';

export const VENDOR_QUOTE_PACK_FIELD_KEYS = [
  'supplierCount',
  'longestLeadMonths',
  'quoteCoverage_pct',
  'topRiskCategories',
  'partnerSupplierCount',
  'comparableQuoteLines',
] as const;

export function mapSupplyChainPlanToCoverage(
  plan: SupplyChainPlan,
): Pick<RequirementAutoFill, 'fields' | 'notes' | 'confidence' | 'fieldsFilledCount' | 'fieldsTotalCount'> {
  const supplierCount = plan.suppliers.length;
  const bomCount = plan.bom.length;
  const quotedItems = new Set(plan.quotes.map(q => q.bomItemId)).size;
  const quoteCoverage_pct = bomCount > 0 ? Math.round((quotedItems / bomCount) * 100) : 0;
  const longestLeadMonths = plan.bom.reduce(
    (max, item) => Math.max(max, item.leadTimeMonthsHigh ?? 0),
    0,
  );
  const topRiskCategories = Array.from(
    new Set(
      plan.bom
        .slice()
        .sort((a, b) => (b.leadTimeMonthsHigh ?? 0) - (a.leadTimeMonthsHigh ?? 0))
        .slice(0, 3)
        .map(item => item.category),
    ),
  ).join(', ');
  const partnerSupplierCount = plan.suppliers.filter(isDatacratePartner).length;
  const comparableQuoteLines = bomIdsWithComparableQuotes(plan).length;

  const fields: Record<string, string | number> = {};
  if (supplierCount > 0) fields.supplierCount = supplierCount;
  if (longestLeadMonths > 0) fields.longestLeadMonths = longestLeadMonths;
  if (bomCount > 0) fields.quoteCoverage_pct = quoteCoverage_pct;
  if (topRiskCategories) fields.topRiskCategories = topRiskCategories;
  if (partnerSupplierCount > 0) fields.partnerSupplierCount = partnerSupplierCount;
  if (comparableQuoteLines > 0) fields.comparableQuoteLines = comparableQuoteLines;

  return {
    fields,
    notes: `Auto-filled from live supply-chain plan (${bomCount} BoM items, ${plan.quotes.length} quotes). Review vendor assumptions before citing.`,
    confidence: {
      tier: Object.keys(fields).length >= 2 ? 'medium' : 'low',
      value: Object.keys(fields).length >= 2 ? 0.6 : 0.45,
      rationale: 'Live BoM / quote registry',
    },
    fieldsFilledCount: Object.keys(fields).length,
    fieldsTotalCount: VENDOR_QUOTE_PACK_FIELD_KEYS.length,
  };
}

export function supplyChainPlanToRequirementAutoFill(plan: SupplyChainPlan): RequirementAutoFill {
  const mapped = mapSupplyChainPlanToCoverage(plan);
  return {
    requirementId: 'vendor-quote-pack',
    ...mapped,
    sourceProvider: 'supply-chain-plan',
    sourceUrl: '',
  };
}
